import { EOriginSystemId } from '@if/core/types'
import { Appear, ProgressTab, ProgressTabs } from '@if/mui'
import { Box, Stack } from '@mui/material'
import { FinancingConfigDto } from '@src/data/api/configs-api/financing-config-api'
import { programStepsByFinancingProgramId } from '@src/data/creditapp-selectors'
import { ECurrentCustomerStep } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCurrentStep, GetContractStatus, GetFundingStatus, GetPrequalStatus } from '../viewCreditApp-selectors'
import StagePanel from './StagePanel'
import ContractStageContent from './stageContent/ContractStageContent'
import CreditStageContent from './stageContent/CreditStageContent'
import FundingStageContent from './stageContent/FundingStageContent'
import IncomeStageContent from './stageContent/IncomeStageContent'
import PrequalStageContent from './stageContent/PrequalStageContent'

interface Props {
  creditApp: CreditApplication
  merchant: Merchant
  financingConfig: FinancingConfigDto
  canEdit: boolean
  isPolling: boolean
}

// Two-column progress view: the selected step's detail (left) and the clickable
// stage list (right). Interim: the left column reuses the existing step tiles.
const ApplicationProgress = ({ creditApp, merchant, financingConfig, canEdit, isPolling }: Props) => {
  const { t } = useTranslation()
  const canEditMerchDb = canEdit && creditApp.originSystemId === EOriginSystemId.MerchantDashboard

  // The steps shown as tabs — the program's own stages, same list the browse rail reads.
  const steps = programStepsByFinancingProgramId(creditApp.financingProgramId)

  // Current step; fall back to the first shown step when it's Draft (not a tab).
  const rawActive = getCurrentStep(creditApp)
  const active = steps.includes(rawActive) ? rawActive : steps[0]

  // The step whose detail is shown on the left (defaults to the current step).
  const [selected, setSelected] = useState<ECurrentCustomerStep>(active)

  // Left column: reskinned StagePanel per done step; interim tile for the rest.
  const renderStageContent = () => {
    switch (selected) {
      case ECurrentCustomerStep.Prequal: {
        const descriptionKey = GetPrequalStatus(creditApp).descriptionKey
        return (
          <StagePanel
            step={selected}
            active={active}
            creditApp={creditApp}
            bannerDescription={descriptionKey ? t(descriptionKey) : undefined}
            steps={steps}
            onNavigate={setSelected}
          >
            <PrequalStageContent creditApp={creditApp} merchant={merchant} />
          </StagePanel>
        )
      }
      case ECurrentCustomerStep.Credit:
        return (
          <StagePanel
            step={selected}
            active={active}
            creditApp={creditApp}
            hideStatusBanner
            steps={steps}
            onNavigate={setSelected}
          >
            <CreditStageContent
              creditApp={creditApp}
              merchant={merchant}
              financingConfig={financingConfig}
              canEdit={canEditMerchDb}
            />
          </StagePanel>
        )
      case ECurrentCustomerStep.IncomeVerification:
        return (
          <StagePanel
            step={selected}
            active={active}
            creditApp={creditApp}
            hideStatusBanner
            steps={steps}
            onNavigate={setSelected}
          >
            <IncomeStageContent creditApp={creditApp} merchant={merchant} canEdit={canEditMerchDb} />
          </StagePanel>
        )
      case ECurrentCustomerStep.Contract: {
        const descriptionKey = GetContractStatus(creditApp, creditApp.worksheet).descriptionKey
        return (
          <StagePanel
            step={selected}
            active={active}
            creditApp={creditApp}
            bannerDescription={descriptionKey ? t(descriptionKey) : undefined}
            steps={steps}
            onNavigate={setSelected}
          >
            <ContractStageContent creditApp={creditApp} isPolling={isPolling} canEdit={canEdit} />
          </StagePanel>
        )
      }
      case ECurrentCustomerStep.Funding: {
        const descriptionKey = GetFundingStatus(creditApp).descriptionKey
        return (
          <StagePanel
            step={selected}
            active={active}
            creditApp={creditApp}
            bannerDescription={descriptionKey ? t(descriptionKey) : undefined}
            steps={steps}
            onNavigate={setSelected}
          >
            <FundingStageContent creditApp={creditApp} />
          </StagePanel>
        )
      }
      default:
        return null
    }
  }

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="stretch">
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
        <Appear key={selected} sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
          {renderStageContent()}
        </Appear>
      </Box>
      <Box sx={{ width: { xs: '100%', lg: 420 }, flexShrink: 0 }}>
        <ProgressTabs
          title={t('viewCreditApplication.applicationProgress')}
          active={active}
          selected={selected}
          onChange={(value) => setSelected(value as ECurrentCustomerStep)}
          hint={t('viewCreditApplication.applicationProgressHint')}
          footer={creditApp.coApplicant ? t('viewCreditApplication.includesCoapplicantVerification') : undefined}
        >
          {steps.map((step) => (
            <ProgressTab key={step} value={step} label={t(`creditStatus.${step}`)} />
          ))}
        </ProgressTabs>
      </Box>
    </Stack>
  )
}

export default ApplicationProgress
